import { useConfig } from "$global-providers/config-provider/provider";
import {
  DebugMode,
  useDevTools,
} from "$global-providers/dev-tools-provider/provider";
import { useFeatureFlagContext } from "$global-providers/feature-flags-provider/provider";
import { useLazyUser } from "$global-providers/user-provider";
import { DEFAULT_LANGUAGE } from "$routes/_lib/use-user-language";
import FormControl from "@material-ui/core/FormControl";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import SwipeableDrawer from "@material-ui/core/SwipeableDrawer";
import { makeStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import OfflineBoltIcon from "@material-ui/icons/OfflineBolt";
import { useEffect, useRef, useState } from "react";

import { ConfigPanel, type Props as ConfigProps } from "./panels/config";
import {
  type Props as FeatureFlagProps,
  FeatureFlagsPanel,
} from "./panels/feature-flags";
import { UserPanel, type Props as UserProps } from "./panels/user";

const useStyles = makeStyles({
  openIcon: {
    position: "fixed",
    bottom: 10,
    left: 10,
    zIndex: 1,
  },
  header: {
    marginLeft: "auto",
    marginRight: "8px",
    marginBlock: "8px",
  },
  versionAndDebugRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 16px 0 16px",
    gap: 16,
  },
  debugDropdown: {
    minWidth: 160,
  },
  body: {
    padding: 20,
    display: "flex",
    justifyContent: "space-between",
    height: 400,
  },
  grid: {
    flexGrow: 1,
  },
});

function useHotKeyToggle(isOpen: boolean, setOpen: (open: boolean) => void) {
  const isOpenRef = useRef(isOpen);
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    document.addEventListener("keydown", (event) => {
      // cmd + e
      if ((event.ctrlKey || event.metaKey) && event.code === "KeyE") {
        setOpen(!isOpenRef.current);
      }
    });
  }, [setOpen]);
}

const DumbDevToolsOverlay: FC<
  {
    appVersion: {
      semantic: string;
      commitHash: string;
      date: string;
    };
  } & FeatureFlagProps &
    ConfigProps &
    UserProps
> = ({
  appVersion,
  config,
  featureFlags,
  onFeatureFlagChange,
  user,
  setUserLanguage,
}) => {
  const [isOpen, setOpen] = useState(false);
  const { debugMode, setDebugMode } = useDevTools();

  useHotKeyToggle(isOpen, setOpen);

  const classes = useStyles();

  return (
    <>
      <IconButton
        aria-label="open dev tools"
        className={classes.openIcon}
        onClick={() => setOpen(true)}
      >
        <Tooltip title="cmd + e" arrow>
          <OfflineBoltIcon fontSize="large" />
        </Tooltip>
      </IconButton>
      <SwipeableDrawer
        anchor="bottom"
        open={isOpen}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
      >
        <div className={classes.versionAndDebugRow}>
          <Typography variant="body2" color="textSecondary">
            {`v${appVersion.semantic} | ${appVersion.commitHash} | ${appVersion.date}`}
          </Typography>
          <FormControl
            variant="outlined"
            size="small"
            className={classes.debugDropdown}
          >
            <InputLabel>Debug Mode</InputLabel>
            <Select
              value={debugMode}
              onChange={(e) => setDebugMode(e.target.value as DebugMode)}
              label="Debug Mode"
            >
              <MenuItem value={DebugMode.None}>None</MenuItem>
              <MenuItem value={DebugMode.Basic}>Basic</MenuItem>
              <MenuItem value={DebugMode.Verbose}>Verbose</MenuItem>
            </Select>
          </FormControl>
        </div>

        <Grid container className={classes.grid} spacing={2}>
          <Grid item xs={4}>
            <FeatureFlagsPanel
              featureFlags={featureFlags}
              onFeatureFlagChange={onFeatureFlagChange}
            />
          </Grid>
          <Grid item xs={4}>
            <UserPanel user={user} setUserLanguage={setUserLanguage} />
          </Grid>
          <Grid item xs={4}>
            <ConfigPanel config={config} />
          </Grid>
        </Grid>
      </SwipeableDrawer>
    </>
  );
};

export const DevToolsOverlay: FC = () => {
  const { devToolsEnabled } = useDevTools();
  const { flags, updateFlags } = useFeatureFlagContext();
  const config = useConfig();
  const user = useLazyUser();
  if (!devToolsEnabled) {
    return null;
  }

  return (
    <DumbDevToolsOverlay
      appVersion={{
        semantic: APP_VERSION_SEMANTIC,
        commitHash: APP_VERSION_COMMIT_HASH,
        date: APP_VERSION_DATE,
      }}
      user={
        user && {
          ...user,
          roleName: user.role,
          language: user.language ?? DEFAULT_LANGUAGE,
        }
      }
      setUserLanguage={() =>
        console.error(
          "this feature was dropped as implementation should change.",
        )
      }
      config={config}
      featureFlags={flags}
      onFeatureFlagChange={updateFlags}
    />
  );
};
